import { useState, useMemo } from 'react';
import {
  ChakraBox,
  ChakraFlex,
  CheckboxV2,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  Text,
  TextAreaInputV2,
  TaskInfoCard,
  AvatarProps,
} from '@equip.health/ui';
import { getTooltipWrapper } from '~/components/maud/inquiries/InquiryDetail/viewUtil';
import { statusOptions } from '~/components/common/Tasks';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { DischargePlanEvents } from '~/analytics/dischargePlanEvents';

interface ReviewIncompleteTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: TaskSummary[];
  justification: string;
  setJustification: (value: string) => void;
  onProceed: () => void;
}
type AvatarUserType = 'provider' | 'patient' | 'support';
const TOP_LEVEL_ID = 'review-incomplete-task-modal';
const PROVIDER = 'provider';

const ReviewIncompleteTaskModal = ({
  isOpen,
  onClose,
  tasks,
  justification,
  setJustification,
  onProceed,
}: ReviewIncompleteTaskModalProps) => {
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const { trackEvent } = useAnalytics();

  const isProceedEnabled = useMemo(
    () => isChecked && justification.trim().length > 0,
    [isChecked, justification],
  );
  const handleProceed = (): void => {
    trackEvent(
      DischargePlanEvents.clickOnProceedButtonInReviewIncompleteModalFromDischargePlan,
      {
        source: AnalyticsSourceValues.DISCHARGE_PLAN_TAB,
      },
    );
    onProceed();
    onClose();
  };
  const handleCancel = (): void => {
    trackEvent(
      DischargePlanEvents.clickOnCancelButtonInReviewIncompleteModalFromDischargePlan,
      {
        source: AnalyticsSourceValues.DISCHARGE_PLAN_TAB,
      },
    );
    setJustification('');
    setIsChecked(false);
    onClose();
  };

  const renderFooter = () => (
    <ChakraFlex justifyContent="space-between" width="100%">
      <FireflyButton
        id={`${TOP_LEVEL_ID}__cancel-button`}
        leftIcon={FAIconName.XmarkRegular}
        onClick={handleCancel}
        variant="secondary"
      >
        Cancel
      </FireflyButton>

      <FireflyButton
        id={`${TOP_LEVEL_ID}__proceed-button`}
        isDisabled={!isProceedEnabled}
        onClick={handleProceed}
        variant="primary"
      >
        Proceed with discharge
      </FireflyButton>
    </ChakraFlex>
  );

  const handleClose = () => {
    trackEvent(
      DischargePlanEvents.clickOnXIconInReviewIncompleteModalFromDischargePlan,
      {
        source: AnalyticsSourceValues.DISCHARGE_PLAN_TAB,
      },
    );
    onClose();
  };

  return (
    <ModalV2
      footerContent={renderFooter()}
      isOpen={isOpen}
      onClose={handleClose}
      size="3xl"
      title="Review incomplete tasks"
    >
      <ChakraBox
        display="flex"
        flexDirection="column"
        gap="24px"
        paddingY="6px"
      >
        <ChakraBox
          display="flex"
          flexDirection="column"
          gap="12px"
          justifyContent="center"
        >
          <Text>The following discharge tasks have not been completed:</Text>

          {tasks?.map((task) => {
            const assignees: AvatarProps[] =
              task.userAssignees?.map((user) => ({
                id: user.externalId,
                name: `${user.firstName} ${user.lastName}`,
                src: user.avatar?.thumbnailUrl || user.avatar?.url,
                userType: user.userType as AvatarUserType,
              })) || [];
            const tooltipWrapper =
              task?.userAssignees?.[0]?.userType?.toLowerCase() === PROVIDER &&
              getTooltipWrapper(
                task?.userAssignees?.[0]?.externalId,
                task?.createdDate,
              );

            return (
              <TaskInfoCard
                assignees={assignees}
                category={task.category}
                id={task.taskExternalId}
                isStatusReadOnly
                key={task.taskExternalId}
                onChangeStatus={undefined}
                status={task.statusCode}
                statusOptions={statusOptions}
                subCategory={task.subCategory}
                title={task.name}
                tooltipWrapper={tooltipWrapper}
              />
            );
          })}
        </ChakraBox>

        <ChakraFlex direction="column" gap="12px">
          <CheckboxV2
            id={`${TOP_LEVEL_ID}__unable-to-complete-checkbox`}
            isChecked={isChecked}
            label="Unable to complete discharge tasks"
            onChange={setIsChecked}
          />

          {isChecked && (
            <ChakraFlex direction="column" pl="34px">
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__justification-input-group`}
                label="Justification"
              >
                <TextAreaInputV2
                  characterLimit={4000}
                  height="70px"
                  id={`${TOP_LEVEL_ID}__justification-input`}
                  onChange={setJustification}
                  placeholder="Please explain"
                  value={justification}
                />
              </InputGroupV2>
            </ChakraFlex>
          )}
        </ChakraFlex>
      </ChakraBox>
    </ModalV2>
  );
};

export default ReviewIncompleteTaskModal;
